import BarChart from 'components/Charts/BarChart';
import DataTable from 'components/Table/DataTable';
import styles from './Dashboard.module.scss';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import SelectBox from 'components/SelectBox/SelectBox';
import SingleCalendar from 'components/SingleCalendar/SingleCalendar';
import Button from 'components/Button/Button';
import { BUTTON_COLOR, BUTTON_SIZE } from 'components/Button/Button.type';
import { useDashboard } from 'hooks/feature/dashboard/useDashborad';
import { useRipeness } from 'hooks/feature/ripeness/useRipeness';
import { CommonOptionType } from 'components/SelectBox/SelectBox.type';
import dayjs from 'dayjs';
import dayjsPluginUTC from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { ColumnDef } from '@tanstack/react-table';
import { DATE_FORMAT } from 'constants/common.constants';
import { EsateRow, EstateData, UserData, UserRow } from 'types/feature/dashboard/dashboard';

const estateColumns: ColumnDef<EsateRow>[] = [
    {
        id: 'no',
        header: 'No',
        cell: ({ row, table }) => table.getRowModel().rows.findIndex(r => r.id === row.id) + 1, // ✅ 정렬 후 순서 반영
    },
    { accessorKey: 'estate', header: 'Estate' },
    {
        accessorKey: 'count',
        header: 'Total',
        cell: ctx => ctx.getValue<number>().toLocaleString(),
    },
];

const userColumns: ColumnDef<UserRow>[] = [
    {
        id: 'no',
        header: 'No',
        cell: ({ row, table }) => table.getRowModel().rows.findIndex(r => r.id === row.id) + 1, // ✅ 정렬 후 순서 반영
    },
    { accessorKey: 'estate', header: 'Estate' },
    { accessorKey: 'name', header: 'Name' },
    {
        accessorKey: 'count',
        header: 'Total',
        cell: ctx => ctx.getValue<number>().toLocaleString(),
    },
];

const ACTIVE_DASHBOARD_TYPE = {
    ESTATE: 'ESTATE',
    MENBER: 'MEMBER',
} as const;

type ChartDataType = {
    label: string[];
    data: number[];
};

type ActiveDashboardType = (typeof ACTIVE_DASHBOARD_TYPE)[keyof typeof ACTIVE_DASHBOARD_TYPE];

const Dashboard = () => {
    const { getEstateChartData, getEstateTableData, getUserTableData, getEstateListByDate } = useDashboard();
    const { getEstateList, getUserList } = useRipeness();

    dayjs.extend(dayjsPluginUTC);
    dayjs.extend(timezone);

    const yesterday = dayjs().subtract(1, 'day').startOf('day');
    const start = yesterday.subtract(7, 'day');

    const [activeDashBoardType, setActiveDashBoardType] = useState<ActiveDashboardType>(ACTIVE_DASHBOARD_TYPE.ESTATE);
    // const [selectedUser, setSelectedUser] = useState<CommonOptionType>();
    const [selectedEstate, setSelectedEstate] = useState<CommonOptionType>();
    // const [userList, setUserList] = useState<CommonOptionType[]>([]);
    const [estateList, setEstateList] = useState<CommonOptionType[]>([]);
    const [startDateValue, setStartDateValue] = useState<Date | null>(start.toDate());
    const [endDateValue, setEndDateValue] = useState<Date | null>(yesterday.toDate());
    const [total, setTotal] = useState<number | undefined>();
    const [chartData, setChartData] = useState<undefined | ChartDataType>();
    const [tableData, setTableData] = useState<(EstateData | UserData)[]>([]);

    const fetchEstateList = async (startDate: Date, endDate: Date) => {
        const result = await getEstateListByDate(
            dayjs(startDate).format(DATE_FORMAT),
            dayjs(endDate).format(DATE_FORMAT)
        );
        if (result) {
            setEstateList(result.map(estate => ({ label: estate.estate, value: estate.estate })));
        }
    };

    // const fetchUserList = async (ver: 'v2' | 'v3', estate?: string) => {
    //     const result = await getUserList(ver, estate);
    //     if (result) {
    //         setUserList(result.map(user => ({ label: user.name ? user.name : 'null', value: user.id })));
    //     }
    // };

    const onChangeEstate = (opt?: CommonOptionType) => {
        setSelectedEstate(opt);
        // setSelectedUser(undefined); // estate 바뀌면 user 선택 초기화
    };

    const onChangeUser = (opt?: CommonOptionType) => {
        // setSelectedUser(opt);
    };

    const handleLoadClick = async (
        startDate: Date = startDateValue!,
        endDate: Date = endDateValue!,
        estate?: string // ✅ estate를 옵셔널로
    ) => {
        if (!startDate || !endDate) return;

        const start = dayjs(startDate).startOf('day');
        const end = dayjs(endDate).startOf('day');
        const max = start.add(31, 'day');

        if (end.isBefore(start) || end.isAfter(max)) {
            const nextStart = end.subtract(7, 'day').startOf('day');
            alert(`It cannot be viewed for more than 30 days.\nTidak dapat dilihat lebih dari 30 hari.`);
            setStartDateValue(nextStart.toDate());
            return;
        }

        if (activeDashBoardType === ACTIVE_DASHBOARD_TYPE.ESTATE) {
            await loadEstate(startDate, endDate, estate);
        } else {
            await loadUser(startDate, endDate, estate);
        }
    };

    const loadEstate = async (startDate: Date, endDate: Date, estateId: string | undefined) => {
        // 여기 api 호출 부 추가

        const chartData = await getEstateChartData(
            dayjs(startDate).format(DATE_FORMAT),
            dayjs(endDate).format(DATE_FORMAT),
            estateId
        );
        const tableData = await getEstateTableData(
            dayjs(startDate).format(DATE_FORMAT),
            dayjs(endDate).format(DATE_FORMAT),
            estateId
        );
        if (tableData) {
            setTableData(tableData);
            const totalCount = tableData.reduce((sum, row) => sum + (row.count ?? 0), 0);
            setTotal(totalCount);
        }
        if (chartData) {
            setChartData({
                label: chartData.labels,
                data: chartData.data,
            });
        }
    };

    const loadUser = async (startDate: Date, endDate: Date, estateId: string | undefined) => {
        // api 호출 부 추가

        const tableData = await getUserTableData(
            dayjs(startDate).format(DATE_FORMAT),
            dayjs(endDate).format(DATE_FORMAT),
            estateId
        );
        if (tableData && (tableData as EsateRow[])) {
            setTableData(tableData);
            const totalCount = tableData.reduce((sum, row) => sum + (row.count ?? 0), 0);
            setTotal(totalCount);
        }
    };

    const handleClearClick = async () => {
        clearData();
        setSelectedEstate(undefined);
        const { start, end } = initDate();
        await handleLoadClick(start, end, undefined); // ✅ 새로운 값으로 바로 조회
    };

    const clearData = () => {
        setChartData(undefined);
        setTableData([]);
        setTotal(undefined);
    };

    const initDate = () => {
        const yesterday = dayjs().subtract(1, 'day').startOf('day');
        const start = yesterday.subtract(7, 'day');
        setStartDateValue(start.toDate());
        setEndDateValue(yesterday.toDate());
        return { start: start.toDate(), end: yesterday.toDate() }; // ✅ 새 값 반환
    };

    useLayoutEffect(() => {
        if (!startDateValue || !endDateValue) return;
        fetchEstateList(startDateValue, endDateValue);

        // fetchUserList('v3');
    }, []);

    // estate 바뀌거나 날짜를 바꾸면
    useEffect(() => {
        // fetchUserList('v3', selectedEstate?.value);
        clearData();
    }, [selectedEstate?.value, startDateValue, endDateValue]);

    useEffect(() => {
        if (!endDateValue || !startDateValue) return;
        // start가 없으면 바로 end-7일로 초기화 (원하지 않으면 이 블록 제거)
        const startStr = dayjs(startDateValue).format(DATE_FORMAT);
        const endStr = dayjs(endDateValue).format(DATE_FORMAT);

        // 날짜 문자열이 완성되지 않았으면 체크 안 함
        if (startStr.length < 10 || endStr.length < 10) {
            return;
        }
        // start가 end보다 크면 초기화
        if (dayjs(startDateValue).isAfter(dayjs(endDateValue))) {
            alert(
                'Start date cannot be later than End date.\nTanggal mulai tidak boleh lebih lambat dari tanggal akhir.'
            );
            setStartDateValue(start.toDate());
            setEndDateValue(yesterday.toDate());
            return;
        }
        // 여기서 처리 한번 하기
        fetchEstateList(startDateValue, endDateValue);
        setSelectedEstate(undefined);
    }, [startDateValue, endDateValue]);

    useEffect(() => {
        const listener = (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                handleLoadClick(startDateValue!, endDateValue!, selectedEstate?.value);
            }
        };
        window.addEventListener('keydown', listener);
        return () => window.removeEventListener('keydown', listener);
    }, [startDateValue, endDateValue, selectedEstate]);

    useEffect(() => {
        // handleClearClick();
        clearData();
        handleLoadClick(startDateValue!, endDateValue!, selectedEstate?.value);
    }, [activeDashBoardType]);

    const tableCoulnm = useMemo(() => {
        return activeDashBoardType === ACTIVE_DASHBOARD_TYPE.ESTATE ? estateColumns : userColumns;
    }, [activeDashBoardType]);

    const disabledLoad = useMemo(() => {
        return startDateValue && endDateValue;
    }, [startDateValue, endDateValue]);

    return (
        <div className={styles['dashboard__container']}>
            <div className={styles['header__container']}>
                <div className={styles['heaer__wrapper']}>
                    <div
                        className={classNames(
                            styles['by-estate'],
                            activeDashBoardType === ACTIVE_DASHBOARD_TYPE.ESTATE && styles['-active']
                        )}
                        onClick={() => setActiveDashBoardType(ACTIVE_DASHBOARD_TYPE.ESTATE)}
                    >
                        Collection Status by Estate
                    </div>
                    <div className={styles['section']}>|</div>
                    <div
                        className={classNames(
                            styles['by-member'],
                            activeDashBoardType === ACTIVE_DASHBOARD_TYPE.MENBER && styles['-active']
                        )}
                        onClick={() => setActiveDashBoardType(ACTIVE_DASHBOARD_TYPE.MENBER)}
                    >
                        Collection Status by User
                    </div>
                </div>
            </div>
            <div className={styles['body__container']}>
                <div className={styles['search__container']}>
                    <div className={styles['search__row']}>
                        <div className={styles['label']}>Date Range</div>
                        <div className={styles['input']}>
                            <SingleCalendar
                                date={startDateValue}
                                maxDate={yesterday.toDate()}
                                onChangeDate={setStartDateValue}
                                resetDate
                                stretch
                            />
                            <div>~</div>
                            <SingleCalendar
                                date={endDateValue}
                                maxDate={yesterday.toDate()}
                                onChangeDate={setEndDateValue}
                                resetDate
                                stretch
                            />
                            <span className={styles['date_info']}>UTC+09:00</span>
                        </div>
                    </div>
                    <div className={styles['search__row']}>
                        <div className={styles['label']}>Category</div>
                        <div className={styles['input']}>
                            <SelectBox
                                width={190}
                                selected={selectedEstate}
                                setSelected={onChangeEstate}
                                placeholder="Select Estate"
                                options={estateList}
                                disabled={estateList.length === 0}
                                clear
                                // isClear
                            />

                            {/* user 정해서 
                            {activeDashBoardType === ACTIVE_DASHBOARD_TYPE.MENBER && (
                                <SelectBox
                                    width={190}
                                    selected={selectedUser}
                                    setSelected={onChangeUser}
                                    placeholder="Select User name"
                                    options={userList}
                                    clear
                                />
                            )} */}
                        </div>
                        <Button
                            onClick={() => handleLoadClick(startDateValue!, endDateValue!, selectedEstate?.value)}
                            disabled={!disabledLoad}
                            size={BUTTON_SIZE[80]}
                            text="Load"
                        ></Button>
                        <Button
                            onClick={handleClearClick}
                            size={BUTTON_SIZE[80]}
                            color={BUTTON_COLOR.white}
                            text="Clear"
                        ></Button>
                    </div>
                </div>
                {activeDashBoardType === ACTIVE_DASHBOARD_TYPE.ESTATE && (
                    <div className={styles['chart__container']}>
                        <div className={styles['chart__body']}>
                            <BarChart
                                labels={chartData?.label}
                                series={chartData?.data}
                                // legend 안보이게 설정
                                options={{
                                    plugins: {
                                        legend: { display: false },
                                    },
                                }}
                            />
                        </div>
                    </div>
                )}
                <div className={styles['table__container']}>
                    <div className={styles['table__header']}>
                        <div className={styles['header__row']}>
                            <div className={styles['table-header__title']}>Total upload</div>
                            <div className={styles['table-header__value']}>
                                {total && total.toLocaleString('en-US')}
                            </div>
                        </div>
                        <div className={styles['header__row']}>
                            <div className={styles['table-header__title']}>Period</div>
                            <div className={styles['table-header__value']}>
                                {startDateValue && (
                                    <div className={styles['']}>{dayjs(startDateValue).format('YYYY-MM-DD')}</div>
                                )}
                                ~
                                {endDateValue && (
                                    <div className={styles['']}>{dayjs(endDateValue).format('YYYY-MM-DD')} </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className={styles['table__body']}>
                        <DataTable columns={tableCoulnm as ColumnDef<EstateData | UserData, any>[]} data={tableData} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
